import React from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';

const Contact = () => {

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Address',
      details: 'Behind Amrut Theatre, Vidyanagar, Hubli-580021',
      color: 'from-blue-400 to-sky-500',
    },
    {
      icon: Phone,
      title: 'Phone',
      details: '0836-2373778 / 2373779',
      color: 'from-sky-400 to-cyan-500',
    },
    {
      icon: Mail,
      title: 'Email',
      details: 'ashokahospitalhbl@gmail.com',
      color: 'from-cyan-400 to-teal-500',
    },
    {
      icon: Clock,
      title: 'Working Hours',
      details: 'Mon-Sat: 8AM-9PM | 24/7 Emergency',
      color: 'from-teal-400 to-emerald-500',
    },
  ];

  return (
    <div className="min-h-screen">

      {/* Hero Section */}
      <section className="relative h-96 flex items-center justify-center overflow-hidden">
        <img
          src="https://images.pexels.com/photos/7089625/pexels-photo-7089625.jpeg"
          alt="Contact"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-sky-900/90 to-blue-900/70"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-5xl md:text-6xl font-bold text-white mb-4"
          >
            Contact <span className="text-sky-300">Us</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-xl text-white/90 max-w-2xl mx-auto"
          >
            Get in touch with us for inquiries or emergency care
          </motion.p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {contactInfo.map((info, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="border-none shadow-lg hover:shadow-xl transition-all h-full">
                  <CardContent className="p-6 text-center">
                    <div
                      className={`w-14 h-14 mx-auto mb-4 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center`}
                    >
                      <info.icon className="w-7 h-7 text-white" />
                    </div>
                    <h3 className="font-bold text-gray-800 mb-2">{info.title}</h3>
                    <p className="text-sm text-gray-600">{info.details}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Full Width Map Section */}
      <section className="py-20 bg-gradient-to-b from-sky-50 to-white">
        <div className="w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="border-none shadow-2xl overflow-hidden rounded-none">
              <div className="h-[80vh] w-full">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1443.6302191235968!2d75.12911476949314!3d15.360769229522004!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bb8d737c8891f1f%3A0x3114f514073e029!2sAshoka%20Multi-Specialty%20and%20Eye%20Hospital!5e0!3m2!1sen!2sin!4v1765801710922!5m2!1sen!2sin"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ashoka Hospital Location"
                ></iframe>
              </div>
            </Card>
          </motion.div>

          {/* Emergency Contact */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="container mx-auto px-4 mt-12"
          >
            <Card className="border-none shadow-2xl bg-gradient-to-br from-red-500 to-red-600 text-white">
              <CardContent className="p-10 text-center">
                <h3 className="text-3xl font-bold mb-4">Emergency Contact</h3>
                <p className="mb-6 text-white/90 text-lg">
                  For medical emergencies, please call our 24/7 emergency hotline immediately.
                </p>
                <div className="flex justify-center sm:justify-center w-full">
                <a href="tel:08362373778" className="w-full sm:w-auto">
                  <Button
                    className="
                      w-full sm:w-auto
                      bg-white text-red-600 hover:bg-gray-100
                      px-4 py-4 sm:px-10 sm:py-6
                      text-sm sm:text-lg
                      font-semibold
                      flex items-center justify-center
                      gap-2
                      rounded-xl
                    "
                  >
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5" />
                    <span className="text-center">
                      Call Emergency:
                      <br className="block sm:hidden" />
                      0836-2373778 / 2373779
                    </span>
                  </Button>
                </a>
              </div>


              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

    </div>
  );
};

export default Contact;
